import React, {ReactNode, useMemo} from "react";

import {InteractiveRegionsMapContext, InteractiveRegionsMapContextValue} from "../context/InteractiveRegionsMapContext";
import {IInteractiveRegion} from "../types/IInteractiveRegion";
import {IInteractiveRegionInput} from "../types/IInteractiveRegionInput";
import {InteractiveRegionsMap} from "./InteractiveRegionsMap";

interface IProps {
    updateRegionsImmediately?: boolean;
    showMainRegionMapOutline?: boolean;
    // TODO: inspect and change the name of `isInModal`, this prop is not related to modal behavior
    isInModal?: boolean;
    tracking?: {
        onMapInit: (params: {regionName: string}) => void;
        onMapUpdate: (params: {regionName: string}) => void;
        onRegionCheckboxToggle: (params: {regionName: string; selectionName: string; action: "on" | "off"}) => void;
    };
    getMainRegion: (parentId: number) => Promise<IInteractiveRegion | null>;
    getDistrictsAndSurroundings: (parentId: number) => Promise<IInteractiveRegion[] | null>;
    selectedRegions: IInteractiveRegionInput[] | null;
    handleUpdateRegions: (selectedRegions: IInteractiveRegion[], parentRegion: {id: number; name: string; slug: string} | null) => void;
    remoteStateSync?: {
        temporaryRegionSelectionState: (string | number)[] | null;
        originRegionSelectionState: (string | number)[] | null;
        syncTemporaryRegionSelection: (selectedRegionIds: (string | number)[]) => void;
        applyRegionSelection: (selectedRegions: (string | number)[]) => void;
    };
    mapBottomLeftSlot?: () => ReactNode;
    bottomControlsSlot?: ((args: {handleSubmitRegions: () => void}) => ReactNode) | false;
    mapHeight?: number;
    mapHeightMobile?: number;
    noMapElement?: React.ReactNode;
}

export const InteractiveRegionsMapAdapter: React.FC<IProps> = ({
    isInModal,
    updateRegionsImmediately,
    showMainRegionMapOutline,
    tracking,
    getMainRegion,
    getDistrictsAndSurroundings,
    selectedRegions,
    handleUpdateRegions,
    remoteStateSync,
    mapBottomLeftSlot,
    bottomControlsSlot,
    mapHeight,
    mapHeightMobile,
    noMapElement
}) => {
    const value = useMemo<InteractiveRegionsMapContextValue>(() => {
        return {
            isInModal,
            updateRegionsImmediately,
            showMainRegionMapOutline,
            tracking,
            selectedRegions,
            getMainRegion,
            getDistrictsAndSurroundings,
            handleUpdateRegions,
            remoteStateSync
        };
    }, [
        isInModal,
        updateRegionsImmediately,
        showMainRegionMapOutline,
        // transform to simple string to avoid unnecessary re-renders
        selectedRegions?.map((region) => region.id).join(",")
    ]);

    return (
        <InteractiveRegionsMapContext.Provider value={value}>
            <InteractiveRegionsMap
                mapBottomLeftSlot={mapBottomLeftSlot}
                bottomControlsSlot={bottomControlsSlot}
                mapHeight={mapHeight}
                mapHeightMobile={mapHeightMobile}
                noMapElement={noMapElement}
            />
        </InteractiveRegionsMapContext.Provider>
    );
};
